import React, { useCallback, useEffect, useState } from "react";
import Statistics from "../components/Statistics";
import InsightsToggleGroup, {
  InsightsToggleOption,
} from "../components/InsightsToggleGroup";
import PageContainer from "../components/PageContainer";
import client from "../api/client";
import { Statistics as StatisticsData } from "../api/jobApplications";
import { usePageCache } from "../hooks/usePageCache";
import { InsightsMode } from "../types/insights";
import { INSIGHTS_PAGE_CHROME } from "./insightsPageChrome";

type InsightsDomain = "all" | "job_hunt" | "finance" | "parenthood" | "business";

const INSIGHTS_DOMAIN_OPTIONS: ReadonlyArray<InsightsToggleOption<InsightsDomain>> = [
  { id: "all", label: "Life View", icon: "🌐" },
  { id: "job_hunt", label: "Job Search", icon: "🔍" },
  { id: "finance", label: "Finance", icon: "💰" },
  { id: "parenthood", label: "Home Mode", icon: "🧸" },
  { id: "business", label: "Business", icon: "🏢" },
];

function buildStatisticsParams(domain: InsightsDomain, mode: InsightsMode) {
  const params: Partial<Record<"domain" | "mode", string>> = {};

  if (domain !== "all") {
    params.domain = domain;
  }

  if (mode !== "operational") {
    params.mode = mode;
  }

  return Object.keys(params).length > 0 ? params : undefined;
}

interface StatisticsSnapshot {
  data: StatisticsData;
  domain: InsightsDomain;
  mode: InsightsMode;
}

const StatisticsPage: React.FC = () => {
  const [filterDomain, setFilterDomain] = useState<InsightsDomain>("all");
  const [filterMode, setFilterMode] = useState<InsightsMode>("operational");

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const params = buildStatisticsParams(filterDomain, filterMode);
      const res = await client.get("/statistics", { params, signal });
      return res.data as StatisticsData;
    },
    [filterDomain, filterMode],
  );

  const { data: stats, loading, error, refresh } = usePageCache<StatisticsData>({
    key: `statistics:${filterDomain}:${filterMode}`,
    fetcher,
    staleMs: 90_000,
  });
  const [displaySnapshot, setDisplaySnapshot] = useState<StatisticsSnapshot | null>(null);
  const currentContextKey = `${filterDomain}:${filterMode}`;
  const previousContextKeyRef = React.useRef(currentContextKey);

  useEffect(() => {
    const contextChanged = previousContextKeyRef.current !== currentContextKey;
    previousContextKeyRef.current = currentContextKey;

    if (contextChanged) {
      return;
    }

    if (stats) {
      setDisplaySnapshot({
        data: stats,
        domain: filterDomain,
        mode: filterMode,
      });
      return;
    }

    if (!loading) {
      setDisplaySnapshot(null);
    }
  }, [currentContextKey, filterDomain, filterMode, loading, stats]);

  const titleId = "insights-page-title";
  const sameDomainSnapshot =
    displaySnapshot?.domain === filterDomain ? displaySnapshot : null;
  const renderedStats = stats ?? sameDomainSnapshot?.data ?? null;
  const entityLoading =
    loading &&
    !stats &&
    Boolean(sameDomainSnapshot) &&
    sameDomainSnapshot?.mode !== filterMode;

  return (
    <PageContainer className="insights-page">
      <header
        className="mb-14 md:mb-16"
        aria-labelledby={titleId}
        data-testid="insights-page-header"
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="h-1 w-8 rounded-full"
            style={{ backgroundColor: "var(--color-accent-indigo)" }}
            aria-hidden
          />
          <p
            className="text-[10px] font-black uppercase tracking-[0.3em]"
            style={{ color: "var(--color-accent-indigo)" }}
            data-testid="insights-eyebrow"
          >
            {INSIGHTS_PAGE_CHROME.eyebrow}
          </p>
        </div>
        <h1
          id={titleId}
          className="text-4xl md:text-5xl font-bold text-theme tracking-tight mb-5 max-w-3xl leading-[1.1] font-['Space_Grotesk',Inter,sans-serif]"
          data-testid="insights-page-title"
        >
          {INSIGHTS_PAGE_CHROME.title}
        </h1>
        <p
          className="text-theme-secondary text-lg md:text-xl max-w-2xl leading-relaxed"
          data-testid="insights-subtitle"
        >
          {INSIGHTS_PAGE_CHROME.subtitle}
        </p>
      </header>

      <InsightsToggleGroup
        label="Insights domain context"
        value={filterDomain}
        options={INSIGHTS_DOMAIN_OPTIONS}
        onChange={setFilterDomain}
        wrapperSx={{ mb: 5 }}
      />

      {loading && !renderedStats ? (
        <div
          className="flex flex-col items-center justify-center py-32 space-y-6"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="h-16 w-16 rounded-3xl border-4 border-indigo-500/10 border-t-indigo-500 animate-spin shadow-2xl shadow-indigo-500/10" />
          <div className="text-theme-muted text-xs font-black uppercase tracking-[0.4em] animate-pulse">
            Loading Insights…
          </div>
        </div>
      ) : error && !renderedStats ? (
        <div
          className="p-10 rounded-3xl border border-red-500/20 bg-red-500/5 text-center max-w-lg mx-auto"
          role="alert"
          aria-live="assertive"
          data-testid="insights-statistics-error"
        >
          <h2 className="text-red-200 font-semibold mb-2 text-base">Couldn&apos;t load Insights</h2>
          <p className="text-red-300/95 text-sm mb-6">{error}</p>
          <button
            type="button"
            onClick={() => refresh()}
            className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest bg-red-500/20 text-red-200 border border-red-400/40 hover:bg-red-500/30 transition-colors"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="fade-in space-y-12 pb-20">
          <Statistics
            stats={renderedStats}
            currentDomain={filterDomain}
            currentMode={filterMode}
            onModeChange={setFilterMode}
            entityLoading={entityLoading}
            onRefreshData={refresh}
          />
        </div>
      )}
    </PageContainer>
  );
};

export default StatisticsPage;
